/**
 * FathomVoyage — Landing Page v2
 * Converted from fathomvoyage-landing-v2.html
 *
 * Fonts:  Fraunces (headings/display) · Figtree (body) · JetBrains Mono (data/UI)
 * Brand:  Amber #e9a020
 * Cookie: fv_last — tracks which section (hero/tools/pulse) the user last engaged with (7-day TTL)
 * CTA:    "Calculate a Voyage" → /calculator (no auth required)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';

// ─── CSS injection ────────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('fv-lp2-css')) {
  const s = document.createElement('style');
  s.id = 'fv-lp2-css';
  s.textContent = `
*, *::before, *::after { box-sizing: border-box; }

.lp-root {
  background: #07090f;
  color: #b8cae0;
  font-family: 'Figtree', sans-serif;
  font-size: 15px;
  line-height: 1.6;
  overflow-x: hidden;
  min-height: 100vh;
}

/* ── grid texture ── */
.lp-grid-bg {
  background-image:
    linear-gradient(#182038 1px, transparent 1px),
    linear-gradient(90deg, #182038 1px, transparent 1px);
  background-size: 52px 52px;
  opacity: 0.25;
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* ── nav ── */
.lp-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 200;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 56px;
  height: 60px;
  background: rgba(7,9,15,0.9);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid #182038;
}
.lp-logo {
  font-family: 'Fraunces', serif;
  font-weight: 900;
  font-size: 18px;
  letter-spacing: -0.02em;
  color: #edf2f8;
  text-decoration: none;
  cursor: pointer;
  background: none; border: none; padding: 0;
}
.lp-logo em { font-style: italic; color: #e9a020; }
.lp-nav-links { display: flex; align-items: center; gap: 40px; }
.lp-nav-link {
  font-family: 'Figtree', sans-serif;
  font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
  color: #6b82a0; text-decoration: none; transition: color 0.2s;
  background: none; border: none; cursor: pointer; padding: 0;
}
.lp-nav-link:hover { color: #edf2f8; }
.lp-nav-signin {
  font-family: 'Figtree', sans-serif;
  font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase;
  padding: 9px 20px;
  border: 1px solid #1e2a45;
  background: transparent; color: #6b82a0;
  cursor: pointer; transition: all 0.2s;
}
.lp-nav-signin:hover { border-color: #e9a020; color: #e9a020; }

/* ── hero ── */
.lp-hero {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  align-items: center;
  padding: 60px 56px 80px;
  gap: 72px;
  position: relative;
  overflow: hidden;
}
.lp-hero-glow {
  position: absolute; top: 30%; left: -5%;
  width: 560px; height: 560px;
  background: radial-gradient(circle, rgba(233,160,32,0.055) 0%, transparent 65%);
  pointer-events: none;
}
.lp-hero-left { position: relative; z-index: 1; }
.lp-hero-kicker {
  font-family: 'Figtree', sans-serif;
  font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase;
  color: #e9a020; margin-bottom: 28px;
  display: flex; align-items: center; gap: 12px;
  animation: lpFadeUp 0.6s 0.05s ease-out both;
}
.lp-hero-kicker::before { content: ''; width: 28px; height: 1px; background: #e9a020; }
.lp-h1 {
  font-family: 'Fraunces', serif;
  font-weight: 900;
  font-size: clamp(38px, 4.2vw, 62px);
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: #edf2f8;
  margin-bottom: 28px; margin-top: 0;
  animation: lpFadeUp 0.6s 0.18s ease-out both;
}
.lp-h1 em { font-style: italic; color: #e9a020; }
.lp-hero-sub {
  font-family: 'Figtree', sans-serif;
  font-size: 18px; color: #ffffff;
  line-height: 1.75; max-width: 400px;
  font-weight: 300;
  margin-bottom: 44px;
  animation: lpFadeUp 0.6s 0.3s ease-out both;
}
.lp-bunker-strip {
  display: inline-flex; align-items: center; gap: 0;
  border: 1px solid #1e2a45;
  background: #0b0f1c;
  margin-bottom: 44px;
  overflow: hidden;
  animation: lpFadeUp 0.6s 0.4s ease-out both;
}
.lp-bs-live {
  display: flex; align-items: center; gap: 7px;
  padding: 10px 14px;
  background: rgba(45,170,110,0.08);
  border-right: 1px solid #1e2a45;
}
.lp-live-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #2daa6e; flex-shrink: 0; position: relative;
}
.lp-live-dot::after {
  content: ''; position: absolute; inset: -4px;
  border-radius: 50%; background: #2daa6e;
  opacity: 0; animation: lpRipple 2.2s ease-out infinite;
}
@keyframes lpRipple {
  0%   { opacity: 0.5; transform: scale(1); }
  100% { opacity: 0;   transform: scale(2.8); }
}
.lp-bs-live span { font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.15em; color: #2daa6e; }
.lp-live-dot.amber { background: #e9a020; }
.lp-live-dot.amber::after { background: #e9a020; }
.lp-bs-item {
  display: flex; flex-direction: column; gap: 1px;
  padding: 10px 18px;
  border-right: 1px solid #182038;
}
.lp-bs-item:last-child { border-right: none; }
.lp-bs-label { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.14em; color: #b7bfca; text-transform: uppercase; }
.lp-bs-val { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: #edf2f8; }
.lp-bs-delta { font-size: 12px; color: #e9a020; }
.lp-hero-actions {
  display: flex; align-items: center; gap: 20px;
  animation: lpFadeUp 0.6s 0.5s ease-out both;
}
.lp-btn-primary {
  font-family: 'Figtree', sans-serif;
  font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  padding: 14px 32px; font-weight: 700;
  background: #e9a020; color: #07090f;
  border: none; cursor: pointer;
  transition: all 0.2s; white-space: nowrap;
}
.lp-btn-primary:hover { background: #f4b535; transform: translateY(-1px); }
.lp-hero-free { font-family: 'Figtree', sans-serif; font-size: 12px; color: #3a4d66; }

/* ── carousel ── */
.lp-hero-right { position: relative; z-index: 1; animation: lpFadeUp 0.7s 0.35s ease-out both; }
.lp-carousel-wrap {
  position: relative;
  border: 1px solid #1e2a45;
  background: #0b0f1c;
  box-shadow: 0 40px 100px rgba(0,0,0,0.55), 0 0 0 1px rgba(233,160,32,0.04);
  aspect-ratio: 16 / 10;
  overflow: hidden;
}
.lp-carousel-bar {
  position: absolute; top: 0; left: 0; right: 0;
  height: 36px;
  background: rgba(0,0,0,0.3);
  border-bottom: 1px solid #182038;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 14px;
  z-index: 10;
}
.lp-c-dots { display: flex; gap: 6px; }
.lp-c-dot { width: 8px; height: 8px; border-radius: 50%; }
.lp-c-title { font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.1em; color: #3a4d66; text-transform: uppercase; }
.lp-c-clock { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #3a4d66; }
.lp-slide {
  position: absolute; inset: 36px 0 0 0;
  display: flex; align-items: center; justify-content: center;
  opacity: 0;
  transition: opacity 0.9s ease;
  pointer-events: none;
}
.lp-slide.active { opacity: 1; pointer-events: auto; }
.lp-slide-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  flex-direction: column; gap: 10px;
  background: #0b0f1c;
  position: relative; overflow: hidden;
}
.lp-slide-placeholder .lp-grid-bg { opacity: 0.4; }
.lp-slide-ph-label {
  font-family: 'JetBrains Mono', monospace; font-size: 11px;
  letter-spacing: 0.15em; text-transform: uppercase;
  color: #3a4d66; position: relative; z-index: 1;
}
.lp-slide-ph-hint {
  font-family: 'Figtree', sans-serif; font-size: 11px;
  color: #3a4d66; font-weight: 400; position: relative; z-index: 1;
}

/* slide indicator dots */
.lp-carousel-indicators { display: flex; gap: 6px; justify-content: center; margin-top: 14px; }
.lp-ci { width: 24px; height: 2px; background: #1e2a45; cursor: pointer; transition: all 0.3s; }
.lp-ci.active { background: #e9a020; width: 32px; }

/* ── pain section ── */
.lp-pain-section {
  padding: 96px 56px;
  border-top: 1px solid #182038;
  position: relative;
}
.lp-section-tag {
  font-family: 'Figtree', sans-serif;
  font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
  color: #e9a020; margin-bottom: 48px;
  display: flex; align-items: center; gap: 14px;
}
.lp-section-tag::after { content: ''; flex: 1; height: 1px; background: #182038; }
.lp-pain-intro {
  font-family: 'Fraunces', serif; font-weight: 700;
  font-size: clamp(22px, 2.5vw, 32px);
  color: #edf2f8; letter-spacing: -0.02em;
  margin-bottom: 52px; max-width: 480px;
  line-height: 1.2; margin-top: 0;
}
.lp-pain-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; background: #182038; border: 1px solid #182038; }
.lp-pain-card {
  background: #07090f;
  padding: 36px 32px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex; flex-direction: column; gap: 16px;
  text-decoration: none; color: inherit;
}
.lp-pain-card:hover { background: #0b0f1c; }
.lp-pain-card:hover .lp-pain-arrow { transform: translateX(4px); color: #e9a020; }
.lp-pain-q {
  font-family: 'Fraunces', serif; font-weight: 700;
  font-size: clamp(20px, 2vw, 28px);
  color: #edf2f8; letter-spacing: -0.02em;
  line-height: 1.15; margin: 0;
}
.lp-pain-context { font-family: 'Figtree', sans-serif; font-size: 13px; color: #6b82a0; font-weight: 400; }
.lp-pain-footer { display: flex; align-items: center; justify-content: space-between; margin-top: auto; }
.lp-pain-tool { font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #e9a020; }
.lp-pain-arrow { font-size: 14px; color: #3a4d66; transition: all 0.2s; }

/* ── tools section ── */
.lp-tools-section {
  padding: 0 56px 96px;
  border-top: 1px solid #182038;
}
.lp-tools-section .lp-section-tag { padding-top: 72px; }
.lp-tool-strip {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  padding: 56px 0;
  border-bottom: 1px solid #182038;
}
.lp-tool-strip:last-child { border-bottom: none; }
.lp-tool-strip.rev { direction: rtl; }
.lp-tool-strip.rev > * { direction: ltr; }
.lp-tool-num { font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.18em; color: #3a4d66; margin-bottom: 20px; }
.lp-tool-name {
  font-family: 'Fraunces', serif; font-weight: 700;
  font-size: clamp(22px, 2.2vw, 30px);
  color: #edf2f8; letter-spacing: -0.02em;
  margin-bottom: 16px; line-height: 1.15; margin-top: 0;
}
.lp-tool-desc { font-family: 'Figtree', sans-serif; font-size: 14px; color: #6b82a0; font-weight: 400; line-height: 1.75; margin-bottom: 24px; }
.lp-tool-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.lp-tool-tag {
  font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.08em;
  padding: 4px 10px; border: 1px solid #1e2a45; color: #c2c7cc;
}
.lp-tool-visual {
  background: #0b0f1c;
  border: 1px solid #1e2a45;
  aspect-ratio: 4/3;
  display: flex; align-items: center; justify-content: center;
  flex-direction: column; gap: 10px;
  position: relative; overflow: hidden;
}
.lp-tv-label { font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; color: #3a4d66; position: relative; z-index: 1; }
.lp-tv-hint { font-family: 'Figtree', sans-serif; font-size: 11px; color: #3a4d66; font-weight: 400; position: relative; z-index: 1; }

/* war risk zone rows inside tool visual */
.lp-wr-zones { display: flex; flex-direction: column; gap: 8px; width: 80%; position: relative; z-index: 1; }
.lp-wrz { display: flex; align-items: center; justify-content: space-between; padding: 7px 12px; background: #07090f; border: 1px solid #1e2a45; }
.lp-wrz-name { font-family: 'Figtree', sans-serif; font-size: 11px; color: #6b82a0; }
.lp-wrz-lev { font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700; }
.lp-wrz-H { color: #d95555; }
.lp-wrz-M { color: #e9a020; }
.lp-wrz-L { color: #2daa6e; }

/* mini sparkline */
.lp-mini-chart { width: 80%; height: 60px; position: relative; z-index: 1; }
.lp-mini-chart svg { width: 100%; height: 100%; }

/* ── pulse section ── */
.lp-pulse-section {
  padding: 96px 56px;
  border-top: 1px solid #182038;
  display: grid; grid-template-columns: 1fr 1.2fr; gap: 80px; align-items: start;
}
.lp-pulse-left .lp-tool-name { margin-bottom: 16px; }
.lp-pulse-left .lp-tool-desc { margin-bottom: 32px; }
.lp-briefing-card { border: 1px solid #1e2a45; background: #0b0f1c; overflow: hidden; }
.lp-bc-header {
  padding: 12px 18px;
  background: rgba(0,0,0,0.25);
  border-bottom: 1px solid #182038;
  display: flex; align-items: center; justify-content: space-between;
}
.lp-bc-title { font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; color: #6b82a0; }
.lp-bc-time { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #3a4d66; }
.lp-bc-body { padding: 20px 18px; display: flex; flex-direction: column; gap: 14px; }
.lp-bc-item { padding-bottom: 14px; border-bottom: 1px solid #182038; }
.lp-bc-item:last-child { border-bottom: none; padding-bottom: 0; }
.lp-bc-tag { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; padding: 2px 7px; display: inline-block; margin-bottom: 6px; }
.lp-bc-tag-alert  { background: rgba(217,85,85,0.08);  color: #d95555; }
.lp-bc-tag-market { background: rgba(233,160,32,0.08); color: #e9a020; }
.lp-bc-tag-ops    { background: rgba(45,170,110,0.08); color: #2daa6e; }
.lp-bc-text { font-family: 'Figtree', sans-serif; font-size: 12px; color: #b8cae0; line-height: 1.65; font-weight: 400; }
.lp-bc-source { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #3a4d66; margin-top: 5px; }
.lp-bc-footer { padding: 10px 18px; border-top: 1px solid #182038; background: rgba(0,0,0,0.15); display: flex; justify-content: space-between; align-items: center; }
.lp-bc-freq { font-family: 'Figtree', sans-serif; font-size: 11px; color: #3a4d66; letter-spacing: 0.08em; }
.lp-bc-badge { font-family: 'JetBrains Mono', monospace; font-size: 11px; padding: 2px 7px; background: rgba(233,160,32,0.08); color: #e9a020; }

/* ── CTA band ── */
.lp-cta-band {
  padding: 96px 56px;
  border-top: 1px solid #182038;
  position: relative; overflow: hidden;
  display: flex; align-items: center; justify-content: space-between; gap: 48px;
}
.lp-cta-glow {
  position: absolute; bottom: -80px; right: 10%;
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(233,160,32,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.lp-cta-text {
  font-family: 'Fraunces', serif; font-weight: 700;
  font-size: clamp(26px, 3vw, 42px);
  letter-spacing: -0.025em; line-height: 1.1;
  color: #edf2f8; max-width: 560px; margin: 0;
}
.lp-cta-text em { font-style: italic; color: #e9a020; }
.lp-cta-right { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: 12px; }
.lp-cta-note { font-family: 'Figtree', sans-serif; font-size: 11px; color: #3a4d66; letter-spacing: 0.05em; text-align: right; }

/* ── footer ── */
.lp-footer {
  border-top: 1px solid #182038;
  padding: 20px 56px;
  display: flex; align-items: center; justify-content: space-between;
}
.lp-f-logo { font-family: 'Fraunces', serif; font-weight: 900; font-size: 14px; color: #3a4d66; }
.lp-f-logo em { font-style: italic; color: #e9a020; opacity: 0.5; }
.lp-f-links { display: flex; gap: 28px; }
.lp-f-link { font-family: 'Figtree', sans-serif; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #3a4d66; text-decoration: none; transition: color 0.2s; }
.lp-f-link:hover { color: #b8cae0; }
.lp-f-copy { font-family: 'Figtree', sans-serif; font-size: 11px; color: #3a4d66; opacity: 0.4; }

/* ── scroll reveal ── */
.lp-reveal { opacity: 0; transform: translateY(18px); transition: opacity 0.65s ease, transform 0.65s ease; }
.lp-visible { opacity: 1; transform: none; }

@keyframes lpFadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── responsive ── */
@media (max-width: 960px) {
  .lp-nav { padding: 0 20px; }
  .lp-hero { grid-template-columns: 1fr; padding: 80px 20px 60px; gap: 48px; min-height: auto; }
  .lp-pain-section,
  .lp-tools-section,
  .lp-pulse-section,
  .lp-cta-band { padding-left: 20px; padding-right: 20px; }
  .lp-pain-grid { grid-template-columns: 1fr; }
  .lp-tool-strip,
  .lp-tool-strip.rev { grid-template-columns: 1fr; direction: ltr; gap: 32px; }
  .lp-pulse-section { grid-template-columns: 1fr; gap: 40px; }
  .lp-cta-band { flex-direction: column; align-items: flex-start; }
  .lp-cta-right { align-items: flex-start; }
  .lp-footer { flex-direction: column; gap: 16px; padding: 20px; text-align: center; }
  .lp-nav-links { gap: 20px; }
}

::-webkit-scrollbar { width: 3px; }
::-webkit-scrollbar-track { background: #07090f; }
::-webkit-scrollbar-thumb { background: #1e2a45; }
`;
  document.head.appendChild(s);
}

const SLIDE_TITLES = ['Voyage Economics', 'Cost Composition', 'Market Intelligence'];
const WORKER_URL_LP = (import.meta.env.VITE_BUNKER_WORKER_URL || '').replace(/\/$/, '');

export default function LandingPage() {
  const navigate = useNavigate();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [clockStr,     setClockStr]     = useState('—');
  const [stripState,   setStripState]   = useState(WORKER_URL_LP ? 'loading' : 'error');
  const [stripData,    setStripData]    = useState(null);

  const heroRef   = useRef(null);
  const toolsRef  = useRef(null);
  const pulseRef  = useRef(null);
  const timerRef  = useRef(null);

  // ── UTC clock ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setClockStr(
        `${String(n.getUTCHours()).padStart(2,'0')}:${String(n.getUTCMinutes()).padStart(2,'0')} UTC`
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // ── bunker strip live fetch ────────────────────────────────────────────────
  useEffect(() => {
    if (!WORKER_URL_LP) return;
    fetch(`${WORKER_URL_LP}/api/bunker-prices`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => {
        if (!d?.ports?.length) { setStripState('error'); return; }
        const find = name => d.ports.find(p => p.port === name);
        const sg  = find('Singapore');
        const fuj = find('Fujairah');
        const rtm = find('Rotterdam');
        setStripData({
          vlsfoSg:   sg  ? { price: sg.vlsfo,    change: sg.vlsfo_change    } : null,
          ifo380Fuj: fuj ? { price: fuj.ifo380,   change: fuj.ifo380_change  } : null,
          mgoRtm:    rtm ? { price: rtm.mgo,      change: rtm.mgo_change     } : null,
        });
        setStripState('live');
      })
      .catch(() => setStripState('error'));
  }, []);

  // ── carousel auto-advance ──────────────────────────────────────────────────
  const startCarouselTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide(s => (s + 1) % SLIDE_TITLES.length);
    }, 4500);
  };

  useEffect(() => {
    startCarouselTimer();
    return () => clearInterval(timerRef.current);
  }, []);

  const goToSlide = (n) => {
    setCurrentSlide(n);
    startCarouselTimer();
  };

  // ── scroll reveal ──────────────────────────────────────────────────────────
  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('lp-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // ── section cookie — fv_last key, 7-day expiry ───────────────────────────
  // Tracks which section (hero / tools / pulse) the user last engaged with.
  useEffect(() => {
    const sections = [
      { key: 'hero',  ref: heroRef  },
      { key: 'tools', ref: toolsRef },
      { key: 'pulse', ref: pulseRef },
    ];
    const observers = sections.map(({ key, ref }) => {
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          document.cookie = `fv_last=${key};path=/;max-age=604800`;
        }
      }, { threshold: 0.4 });
      if (ref.current) obs.observe(ref.current);
      return obs;
    });
    return () => observers.forEach(obs => obs.disconnect());
  }, []);

  const handleCalculate = () => navigate('/calculator');

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="lp-root">

      <Navbar />

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="lp-hero" id="hero" ref={heroRef}>
        <div className="lp-grid-bg" />
        <div className="lp-hero-glow" />

        <div className="lp-hero-left">
          <div className="lp-hero-kicker">Voyage Cost Intelligence</div>
          <h1 className="lp-h1">What will this voyage <em>actually</em> cost me?</h1>
          <p className="lp-hero-sub">
            Bunker prices, war risk premiums, route P&amp;L — assembled in one place.
            Before your next fixture call.
          </p>

          {/* live bunker strip */}
          <div className="lp-bunker-strip">
            {stripState !== 'error' && (
              <div className="lp-bs-live">
                <div className={`lp-live-dot${stripState === 'loading' ? ' amber' : ''}`} />
                <span style={{ color: stripState === 'loading' ? '#e9a020' : '#2daa6e' }}>
                  {stripState === 'loading' ? 'Loading…' : 'Live'}
                </span>
              </div>
            )}
            {[
              { label: 'VLSFO Singapore', d: stripData?.vlsfoSg   },
              { label: 'IFO380 Fujairah', d: stripData?.ifo380Fuj  },
              { label: 'MGO ARA',         d: stripData?.mgoRtm     },
            ].map(({ label, d }) => {
              const price  = d?.price;
              const change = d?.change;
              return (
                <div key={label} className="lp-bs-item">
                  <span className="lp-bs-label">{label}</span>
                  <span className="lp-bs-val">
                    {price != null ? `$${price}` : '—'}
                    {change != null && (
                      <span className="lp-bs-delta">
                        {' '}{change > 0 ? `▲ +$${change}` : `▼ $${Math.abs(change)}`}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="lp-hero-actions">
            <button className="lp-btn-primary" onClick={handleCalculate}>
              Calculate a Voyage →
            </button>
            <span className="lp-hero-free">Free — no card required</span>
          </div>
        </div>

        {/* ── CAROUSEL ── */}
        <div className="lp-hero-right">
          <div className="lp-carousel-wrap" id="carousel">

            <div className="lp-carousel-bar">
              <div className="lp-c-dots">
                <div className="lp-c-dot" style={{ background: '#d95555' }} />
                <div className="lp-c-dot" style={{ background: '#e9a020' }} />
                <div className="lp-c-dot" style={{ background: '#2daa6e' }} />
              </div>
              <div className="lp-c-title">{SLIDE_TITLES[currentSlide]}</div>
              <div className="lp-c-clock">{clockStr}</div>
            </div>

            {/* ── SLIDE 1: Voyage Economics ────────────────────────────────── */}
            {/*
              SCREENSHOT SLOT — Slide 1 (Voyage Economics / Route Cards)
              Replace the placeholder div below with:
              <img
                src="/screenshots/route-cards.png"
                alt="Voyage Economics — Route Cost Cards"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }}
              />
            */}
            <div className={`lp-slide${currentSlide === 0 ? ' active' : ''}`}>
              <div className="lp-slide-placeholder">
                <div className="lp-grid-bg" />
                <span className="lp-slide-ph-label">Screenshot — Voyage Economics</span>
                <span className="lp-slide-ph-hint">Route cost cards · War risk overlay</span>
              </div>
            </div>

            {/* ── SLIDE 2: Cost Composition ────────────────────────────────── */}
            {/*
              SCREENSHOT SLOT — Slide 2 (Cost Composition Bars)
              Replace the placeholder div below with:
              <img
                src="/screenshots/cost-composition.png"
                alt="Cost Composition — Bunker, Canal, War Risk, TC breakdown"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }}
              />
            */}
            <div className={`lp-slide${currentSlide === 1 ? ' active' : ''}`}>
              <div className="lp-slide-placeholder">
                <div className="lp-grid-bg" />
                <span className="lp-slide-ph-label">Screenshot — Cost Composition</span>
                <span className="lp-slide-ph-hint">Bunker · Canal · War Risk · TC breakdown</span>
              </div>
            </div>

            {/* ── SLIDE 3: Market Intelligence ─────────────────────────────── */}
            {/*
              SCREENSHOT SLOT — Slide 3 (Market Intelligence / Next Cargo)
              Replace the placeholder div below with:
              <img
                src="/screenshots/market-intelligence.png"
                alt="Market Intelligence — Next cargo position"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }}
              />
            */}
            <div className={`lp-slide${currentSlide === 2 ? ' active' : ''}`}>
              <div className="lp-slide-placeholder">
                <div className="lp-grid-bg" />
                <span className="lp-slide-ph-label">Screenshot — Market Intelligence</span>
                <span className="lp-slide-ph-hint">Next cargo position · Trade flow data</span>
              </div>
            </div>

          </div>

          {/* slide indicators */}
          <div className="lp-carousel-indicators">
            {SLIDE_TITLES.map((_, i) => (
              <div
                key={i}
                className={`lp-ci${currentSlide === i ? ' active' : ''}`}
                onClick={() => goToSlide(i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── PAIN POINTS ──────────────────────────────────────────────────────── */}
      <section className="lp-pain-section">
        <div className="lp-grid-bg" style={{ opacity: 0.12 }} />
        <div className="lp-section-tag">Questions you&apos;re asking every day</div>
        <h2 className="lp-pain-intro">
          The answers are all here.<br />In one place.
        </h2>

        <div className="lp-pain-grid">
          <Link to="/bunker" className="lp-pain-card lp-reveal">
            <div className="lp-pain-q">Where to bunker?</div>
            <div className="lp-pain-context">
              Live VLSFO, MGO and IFO380 across 20+ ports with a 4-hour refresh.
            </div>
            <div className="lp-pain-footer">
              <span className="lp-pain-tool">Bunker Monitor →</span>
              <span className="lp-pain-arrow">↗</span>
            </div>
          </Link>

          <Link to="/routes" className="lp-pain-card lp-reveal">
            <div className="lp-pain-q">Suez or Cape<br />this week?</div>
            <div className="lp-pain-context">
              Full P&amp;L comparison including current war risk premiums and bunker costs.
            </div>
            <div className="lp-pain-footer">
              <span className="lp-pain-tool">Route Calculator →</span>
              <span className="lp-pain-arrow">↗</span>
            </div>
          </Link>

          <Link to="/war-risk" className="lp-pain-card lp-reveal">
            <div className="lp-pain-q">What&apos;s war risk<br />costing me?</div>
            <div className="lp-pain-context">
              JWC-aligned zones with live premium rates — Red Sea, Hormuz, Gulf of Guinea.
            </div>
            <div className="lp-pain-footer">
              <span className="lp-pain-tool">War Risk Overlay →</span>
              <span className="lp-pain-arrow">↗</span>
            </div>
          </Link>

          <Link to="/calculator" className="lp-pain-card lp-reveal">
            <div className="lp-pain-q">Will this fix<br />hurt our CII?</div>
            <div className="lp-pain-context">
              CII impact indicator with CO₂ comparison across route alternatives.
            </div>
            <div className="lp-pain-footer">
              <span className="lp-pain-tool">Route Calculator →</span>
              <span className="lp-pain-arrow">↗</span>
            </div>
          </Link>
        </div>
      </section>

      {/* ── TOOLS ────────────────────────────────────────────────────────────── */}
      <section className="lp-tools-section" id="tools" ref={toolsRef}>
        <div className="lp-section-tag">What&apos;s inside</div>

        {/* 01 — Bunker Monitor */}
        <div className="lp-tool-strip lp-reveal" id="bunker">
          <div className="lp-tool-text">
            <div className="lp-tool-num">01 — Bunker Monitor</div>
            <h3 className="lp-tool-name">Global bunker prices.<br />Right now.</h3>
            <p className="lp-tool-desc">
              Live VLSFO, MGO and IFO380 prices across 20+ major bunkering ports.
              Updated every 4 hours. Historical trend overlay so you know if today&apos;s
              price is a window or a trap.
            </p>
            <div className="lp-tool-tags">
              <span className="lp-tool-tag">VLSFO</span>
              <span className="lp-tool-tag">MGO</span>
              <span className="lp-tool-tag">IFO380</span>
              <span className="lp-tool-tag">4-hr refresh</span>
              <span className="lp-tool-tag">20+ ports</span>
            </div>
          </div>
          <div className="lp-tool-visual">
            <div className="lp-grid-bg" style={{ opacity: 0.4 }} />
            {/*
              SCREENSHOT SLOT — Bunker Monitor
              <img src="/screenshots/bunker-monitor.png" alt="Bunker Monitor" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            */}
            <div className="lp-mini-chart">
              <svg viewBox="0 0 200 60" preserveAspectRatio="none">
                <polyline
                  points="0,45 20,38 40,42 60,30 80,35 100,28 120,32 140,20 160,25 180,18 200,22"
                  fill="none" stroke="#e9a020" strokeWidth="1.5"
                />
                <polyline
                  points="0,45 20,38 40,42 60,30 80,35 100,28 120,32 140,20 160,25 180,18 200,22 200,60 0,60"
                  fill="rgba(233,160,32,0.06)" stroke="none"
                />
              </svg>
            </div>
            <span className="lp-tv-label">Screenshot — Bunker Monitor</span>
          </div>
        </div>

        {/* 02 — War Risk */}
        <div className="lp-tool-strip rev lp-reveal" id="war-risk">
          <div className="lp-tool-text">
            <div className="lp-tool-num">02 — War Risk</div>
            <h3 className="lp-tool-name">Know the zones<br />before you fix.</h3>
            <p className="lp-tool-desc">
              JWC-aligned risk zones mapped with current premium rates. Red Sea, Persian Gulf,
              Strait of Hormuz, Gulf of Guinea, Black Sea. Updated weekly. Actual cost to your
              P&amp;L, not just a map.
            </p>
            <div className="lp-tool-tags">
              <span className="lp-tool-tag">JWC-aligned</span>
              <span className="lp-tool-tag">Hull premiums</span>
              <span className="lp-tool-tag">Weekly update</span>
              <span className="lp-tool-tag">6 active zones</span>
            </div>
          </div>
          <div className="lp-tool-visual">
            <div className="lp-grid-bg" style={{ opacity: 0.4 }} />
            {/*
              SCREENSHOT SLOT — War Risk
              <img src="/screenshots/war-risk.png" alt="War Risk Zones" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            */}
            <div className="lp-wr-zones">
              <div className="lp-wrz">
                <span className="lp-wrz-name">Red Sea / Bab-el-Mandeb</span>
                <span className="lp-wrz-lev lp-wrz-H">HIGH</span>
              </div>
              <div className="lp-wrz">
                <span className="lp-wrz-name">Strait of Hormuz</span>
                <span className="lp-wrz-lev lp-wrz-H">HIGH</span>
              </div>
              <div className="lp-wrz">
                <span className="lp-wrz-name">Gulf of Guinea</span>
                <span className="lp-wrz-lev lp-wrz-M">MEDIUM</span>
              </div>
              <div className="lp-wrz">
                <span className="lp-wrz-name">Black Sea</span>
                <span className="lp-wrz-lev lp-wrz-M">MEDIUM</span>
              </div>
              <div className="lp-wrz">
                <span className="lp-wrz-name">North Arabian Sea</span>
                <span className="lp-wrz-lev lp-wrz-L">LOW</span>
              </div>
            </div>
            <span className="lp-tv-label" style={{ marginTop: 8 }}>Screenshot — War Risk</span>
          </div>
        </div>

        {/* 03 — Route Calculator */}
        <div className="lp-tool-strip lp-reveal" id="routes">
          <div className="lp-tool-text">
            <div className="lp-tool-num">03 — Route Calculator</div>
            <h3 className="lp-tool-name">Total voyage cost.<br />Before the call.</h3>
            <p className="lp-tool-desc">
              Full voyage P&amp;L — bunker, port costs, canal dues, war risk, broker commissions.
              Compare laden vs ballast. Suez vs Cape. See where the cost is hiding before you
              commit to a fixture.
            </p>
            <div className="lp-tool-tags">
              <span className="lp-tool-tag">Full P&amp;L</span>
              <span className="lp-tool-tag">Route comparison</span>
              <span className="lp-tool-tag">CII indicator</span>
              <span className="lp-tool-tag">PSC flag</span>
              <span className="lp-tool-tag">Ballast leg</span>
            </div>
          </div>
          <div className="lp-tool-visual">
            <div className="lp-grid-bg" style={{ opacity: 0.4 }} />
            {/*
              SCREENSHOT SLOT — Route Calculator (primary hero screenshot)
              <img src="/screenshots/route-calculator.png" alt="Route Calculator" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            */}
            <span className="lp-tv-label">Screenshot — Route Calculator</span>
            <span className="lp-tv-hint">primary hero screenshot</span>
          </div>
        </div>
      </section>

      {/* ── MARITIME PULSE ───────────────────────────────────────────────────── */}
      <section className="lp-pulse-section" id="pulse" ref={pulseRef}>
        <div className="lp-pulse-left">
          <div className="lp-section-tag" style={{ marginBottom: 32 }}>Maritime Pulse</div>
          <h3 className="lp-tool-name">Your morning briefing.<br />Before the market opens.</h3>
          <p className="lp-tool-desc">
            Six-hourly synthesis of what matters — UKMTO alerts, bunker market moves,
            port disruptions. Not a news feed. A briefing. The way your secretary would write it
            if your secretary read five sources and gave you only the relevant parts.
          </p>
          <div className="lp-tool-tags">
            <span className="lp-tool-tag">6-hourly</span>
            <span className="lp-tool-tag">5 sources</span>
            <span className="lp-tool-tag">AI synthesised</span>
          </div>
        </div>

        <div className="lp-briefing-card lp-reveal">
          <div className="lp-bc-header">
            <span className="lp-bc-title">Maritime Pulse</span>
            <span className="lp-bc-time">{clockStr}</span>
          </div>
          <div className="lp-bc-body">
            <div className="lp-bc-item">
              <span className="lp-bc-tag lp-bc-tag-alert">Alert</span>
              <p className="lp-bc-text">
                UKMTO reports two separate drone incidents in the southern Red Sea overnight.
                Vessels transiting Bab-el-Mandeb advised to maintain heightened bridge watch
                and report positions.
              </p>
              <div className="lp-bc-source">Source: UKMTO · 04:22 UTC</div>
            </div>
            <div className="lp-bc-item">
              <span className="lp-bc-tag lp-bc-tag-market">Bunker</span>
              <p className="lp-bc-text">
                Singapore VLSFO firmed $6/mt this morning on tighter supply signals out of
                Fujairah. ARA MGO unchanged. Spread between Singapore and Rotterdam now at
                12-week high.
              </p>
              <div className="lp-bc-source">Source: Ship &amp; Bunker · 06:10 UTC</div>
            </div>
            <div className="lp-bc-item">
              <span className="lp-bc-tag lp-bc-tag-ops">Operations</span>
              <p className="lp-bc-text">
                Panama Canal waiting times returned to normal — average 0.8 days northbound.
                Drought conditions eased. No draft restrictions currently in effect.
              </p>
              <div className="lp-bc-source">Source: Hellenic Shipping News · 05:45 UTC</div>
            </div>
          </div>
          <div className="lp-bc-footer">
            <span className="lp-bc-freq">Updates every 6 hours</span>
            <span className="lp-bc-badge">Live</span>
          </div>
        </div>
      </section>

      {/* ── CTA BAND ─────────────────────────────────────────────────────────── */}
      <div className="lp-cta-band">
        <div className="lp-cta-glow" />
        <p className="lp-cta-text">
          Every fixture decision starts with a number.<br />
          <em>Now you have it.</em>
        </p>
        <div className="lp-cta-right">
          <button className="lp-btn-primary" onClick={handleCalculate}>
            Calculate a Voyage →
          </button>
          <p className="lp-cta-note">Free — no card required. No demo call.</p>
        </div>
      </div>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-f-logo">Fathom<em>Voyage</em></div>
        <div className="lp-f-links">
          <Link to="/privacy" className="lp-f-link">Privacy</Link>
          <Link to="/terms"   className="lp-f-link">Terms</Link>
        </div>
        <div className="lp-f-copy">© 2026 FathomVoyage</div>
      </footer>

    </div>
  );
}
